import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export const ShipmentLabelViewer = ({ document, onClose }) => {
  const canvas = useRef(null);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  useEffect(() => { setPage(1); setPages(0); }, [document]);
  useEffect(() => {
    if (!document) return;
    let disposed = false, task, render;
    setLoading(true); setError("");
    (async () => {
      try {
        const pdfjs = await import("pdfjs-dist/legacy/build/pdf.mjs");
        pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/legacy/build/pdf.worker.min.mjs", import.meta.url).toString();
        if (disposed) return;
        task = pdfjs.getDocument({ data: await document.blob.arrayBuffer(), isEvalSupported: false, useSystemFonts: true });
        const pdf = await task.promise;
        if (disposed) return;
        setPages(pdf.numPages);
        const sheet = await pdf.getPage(Math.min(page, pdf.numPages));
        if (disposed || !canvas.current) return;
        const viewport = sheet.getViewport({ scale: 1.5 });
        canvas.current.width = viewport.width; canvas.current.height = viewport.height;
        render = sheet.render({ canvasContext: canvas.current.getContext("2d"), viewport });
        await render.promise;
      } catch (failure) { if (!disposed) setError("Non riesco a visualizzare il PDF. Chiudi e riapri la lettera di vettura."); }
      finally { if (!disposed) setLoading(false); }
    })();
    return () => { disposed = true; render?.cancel(); task?.destroy(); };
  }, [document, page]);
  return <Dialog open={Boolean(document)} onOpenChange={(open) => { if (!open) onClose(); }}><DialogContent className="max-w-3xl max-h-[92dvh] overflow-y-auto" data-testid="shipment-label-preview"><DialogHeader><DialogTitle>Lettera di vettura</DialogTitle><DialogDescription>Anteprima del PDF originale del corriere, senza download automatico.</DialogDescription></DialogHeader>
    {loading && <p role="status" className="flex gap-2 items-center text-sm" data-testid="shipment-label-loading"><Loader2 className="w-4 h-4 animate-spin" />Apertura documento…</p>}
    {error && <p role="alert" className="text-destructive text-sm" data-testid="shipment-label-error">{error}</p>}
    <canvas ref={canvas} className={`max-w-full h-auto mx-auto bg-white ${loading || error ? "hidden" : ""}`} aria-label={`Lettera di vettura, pagina ${page}`} data-testid="shipment-label-canvas" />
    {pages > 1 && <div className="flex justify-center items-center gap-3"><Button size="icon" variant="outline" disabled={loading || page === 1} onClick={() => setPage(page - 1)} aria-label="Pagina precedente" data-testid="shipment-label-prev"><ChevronLeft /></Button><span data-testid="shipment-label-pages">{page} / {pages}</span><Button size="icon" variant="outline" disabled={loading || page === pages} onClick={() => setPage(page + 1)} aria-label="Pagina successiva" data-testid="shipment-label-next"><ChevronRight /></Button></div>}
  </DialogContent></Dialog>;
};
